import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewsletterForm = ({ source = "homepage", compact = false, dark = false }) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please tick the consent box so we know you'd like to hear from us.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, {
        first_name: firstName.trim(),
        email: email.trim(),
        consent,
        source,
      });
      track("newsletter_submit", { source, status: data.status });
      if (data.status === "existing") {
        toast.info("You're already part of the community. Thank you for being here.");
      } else {
        toast.success(`Welcome, ${firstName.trim()}. You're part of the community now.`);
      }
      setFirstName("");
      setEmail("");
      setConsent(false);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again in a moment.");
      track("newsletter_submit", { source, status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const inputCls = dark
    ? "w-full rounded-full border border-ivory/25 bg-ivory/10 px-5 py-3.5 text-sm text-ivory placeholder:text-ivory/50 outline-none transition-colors duration-300 focus:border-gold-soft"
    : "w-full rounded-full border border-forest/20 bg-white/70 px-5 py-3.5 text-sm text-charcoal placeholder:text-taupe/70 outline-none transition-colors duration-300 focus:border-gold focus:bg-white";

  return (
    <form data-testid={`newsletter-form-${source}`} onSubmit={onSubmit} noValidate={false}>
      <div className={compact ? "flex flex-col gap-3" : "flex flex-col gap-3 sm:flex-row"}>
        <label className="sr-only" htmlFor={`nl-name-${source}`}>First name</label>
        <input
          id={`nl-name-${source}`}
          data-testid={`newsletter-first-name-${source}`}
          type="text"
          required
          maxLength={80}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name"
          className={inputCls}
          autoComplete="given-name"
        />
        <label className="sr-only" htmlFor={`nl-email-${source}`}>Email address</label>
        <input
          id={`nl-email-${source}`}
          data-testid={`newsletter-email-${source}`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          className={inputCls}
          autoComplete="email"
        />
      </div>
      <label
        className={`mt-4 flex cursor-pointer items-start gap-3 text-xs leading-relaxed ${
          dark ? "text-ivory/70" : "text-charcoal/65"
        }`}
      >
        <input
          type="checkbox"
          data-testid={`newsletter-consent-${source}`}
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="brand-check mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
        />
        <span>
          I'd like to receive Staci's emails and understand I can unsubscribe at any time. See
          the{" "}
          <a
            href="#privacy"
            data-testid={`privacy-policy-link-${source}`}
            onClick={(e) => {
              e.preventDefault();
              toast.info("The privacy policy will be published here before launch.");
            }}
            className={`underline underline-offset-2 ${dark ? "text-gold-soft" : "text-forest"}`}
          >
            privacy policy
          </a>
          .
        </span>
      </label>
      <button
        type="submit"
        data-testid={`newsletter-submit-${source}`}
        disabled={loading}
        className={`mt-5 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-all duration-300 disabled:opacity-60 ${
          dark
            ? "bg-gold text-forest-deep hover:bg-gold-soft"
            : "bg-forest text-ivory hover:bg-forest-deep hover:shadow-[0_12px_30px_rgba(23,63,53,0.25)]"
        } ${compact ? "w-full" : ""}`}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        Join the Community
      </button>
    </form>
  );
};
